import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Wrench, Gauge, ClipboardText, Package, ChartBar, ChartLineUp, ListChecks, WarningOctagon, TestTube, Heartbeat, Plus, ArrowClockwise, Trash, FilePdf, PencilSimple, X, NotePencil } from "@phosphor-icons/react";
import BitacoraTab from "@/pages/maintenance/BitacoraTab";
import InspectionsTab from "@/pages/maintenance/InspectionsTab";
import ParetoTab from "@/pages/maintenance/ParetoTab";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, ReferenceLine } from "recharts";
import { api, apiDelete, apiError } from "@/lib/api";
import ProjectBadge from "@/components/ProjectBadge";
import ProjectField, { assertProject, defaultProject } from "@/components/ProjectField";
import { useConfirmDelete } from "@/components/ConfirmDeleteDialog";
import { useAuth } from "@/context/AuthContext";
import { usePermissions } from "@/lib/permissions";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { MagnifyingGlassPlus } from "@phosphor-icons/react";
import SectionHeader from "@/components/SectionHeader";
import { reloadEquipmentCache } from "@/components/EquipmentPicker";
import { useOpsProjects, getOpsProject } from "@/components/OpsProjectSelector";

const SEM_COLOR = {
  VERDE: "bg-emerald-500 text-white",
  AMARILLO: "bg-amber-400 text-slate-900",
  ROJO: "bg-red-600 text-white",
  NEGRO: "bg-slate-900 text-white",
  "N/A": "bg-slate-300 text-slate-700",
};

export default function Maintenance() {
  const { user } = useAuth();
  const { has } = usePermissions();
  const canWrite = has("maintenance.write");
  const [tab, setTab] = useState("componentes");
  const [equipment, setEquipment] = useState([]);
  const [components, setComponents] = useState([]);
  const [orders, setOrders] = useState([]);
  const [kits, setKits] = useState([]);
  const [forecast, setForecast] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [catalog, setCatalog] = useState({ order_kinds: [], kit_kinds: [] });
  const [reliability, setReliability] = useState(null);
  const [failures, setFailures] = useState([]);

  const loadAll = async () => {
    try {
      const [eq, co, ors, kt, fc, kp, ch, cat, rel, flr] = await Promise.all([
        api.get("/maintenance/equipment").then((r) => r.data),
        api.get("/maintenance/components").then((r) => r.data),
        api.get("/maintenance/orders").then((r) => r.data),
        api.get("/maintenance/kits").then((r) => r.data),
        api.get("/maintenance/forecast").then((r) => r.data),
        api.get("/maintenance/kpis").then((r) => r.data),
        api.get("/maintenance/planner-checklist").then((r) => r.data),
        api.get("/maintenance/catalog").then((r) => r.data),
        api.get("/maintenance/reliability").then((r) => r.data),
        api.get("/maintenance/failures").then((r) => r.data),
      ]);
      setEquipment(eq); setComponents(co); setOrders(ors); setKits(kt);
      setForecast(fc); setKpis(kp); setChecklist(ch); setCatalog(cat);
      setReliability(rel); setFailures(flr);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { loadAll(); }, []);

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-6" data-testid="maintenance-page">
      <SectionHeader icon={Wrench} title="Mantenimiento Diésel" eyebrow="Programación · Confiabilidad · Reportes" size="lg" testid="mnt-header" />

      {kpis && <KpiStrip kpis={kpis} />}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-100 grid grid-cols-2 md:grid-cols-11 h-auto">
          <TabsTrigger data-testid="mnt-tab-componentes" value="componentes"><Gauge size={14} className="mr-1" /> Componentes</TabsTrigger>
          <TabsTrigger data-testid="mnt-tab-programa" value="programa"><ClipboardText size={14} className="mr-1" /> Programa Semanal</TabsTrigger>
          <TabsTrigger data-testid="mnt-tab-inspecciones" value="inspecciones"><MagnifyingGlassPlus size={14} className="mr-1" /> Inspecciones</TabsTrigger>
          <TabsTrigger data-testid="mnt-tab-kits" value="kits"><Package size={14} className="mr-1" /> Kits</TabsTrigger>
          <TabsTrigger data-testid="mnt-tab-forecast" value="forecast"><ChartLineUp size={14} className="mr-1" /> Pronóstico</TabsTrigger>
          <TabsTrigger data-testid="mnt-tab-kpis" value="kpis"><ChartBar size={14} className="mr-1" /> KPIs</TabsTrigger>
          <TabsTrigger data-testid="mnt-tab-confiabilidad" value="confiabilidad"><Heartbeat size={14} className="mr-1" /> Confiabilidad</TabsTrigger>
          <TabsTrigger data-testid="mnt-tab-pareto" value="pareto"><ChartBar size={14} className="mr-1" /> Pareto</TabsTrigger>
          <TabsTrigger data-testid="mnt-tab-bathtub" value="bathtub"><ChartLineUp size={14} className="mr-1" /> Análisis por equipo</TabsTrigger>
          <TabsTrigger data-testid="mnt-tab-operador" value="operador"><ListChecks size={14} className="mr-1" /> Operador</TabsTrigger>
          <TabsTrigger data-testid="mnt-tab-flota" value="flota"><Wrench size={14} className="mr-1" /> Flota</TabsTrigger>
          <TabsTrigger data-testid="mnt-tab-bitacora" value="bitacora"><NotePencil size={14} className="mr-1" /> Bitácora</TabsTrigger>
        </TabsList>

        <TabsContent value="componentes">
          <ComponentsTab equipment={equipment} components={components} canWrite={canWrite} reload={loadAll} />
        </TabsContent>
        <TabsContent value="programa">
          <ProgramTab equipment={equipment} orders={orders} kits={kits} canWrite={canWrite} kpis={kpis}
                      orderKinds={catalog.order_kinds} reload={loadAll} />
        </TabsContent>
        <TabsContent value="inspecciones">
          <InspectionsTab canWrite={canWrite} />
        </TabsContent>
        <TabsContent value="kits">
          <KitsTab kits={kits} canWrite={canWrite} kitKinds={catalog.kit_kinds} reload={loadAll} />
        </TabsContent>
        <TabsContent value="forecast">
          <ForecastTab forecast={forecast} />
        </TabsContent>
        <TabsContent value="kpis">
          <KpisTab kpis={kpis} checklist={checklist} />
        </TabsContent>
        <TabsContent value="confiabilidad">
          <ReliabilityTab reliability={reliability} failures={failures}
                          equipment={equipment} canWrite={canWrite} reload={loadAll} />
        </TabsContent>
        <TabsContent value="pareto">
          <ParetoTab equipment={equipment} />
        </TabsContent>
        <TabsContent value="bathtub">
          <BathtubTab equipment={equipment} />
        </TabsContent>
        <TabsContent value="operador">
          <OperatorMetricsTab />
        </TabsContent>
        <TabsContent value="flota">
          <FleetTab equipment={equipment} canWrite={canWrite} reload={loadAll} />
        </TabsContent>
        <TabsContent value="bitacora">
          <BitacoraTab equipment={equipment} canWrite={canWrite} onReload={loadAll} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ============================================================================
// KPI STRIP (siempre visible arriba)
// ============================================================================
function KpiStrip({ kpis }) {
  const items = [
    { label: "HH Programadas", value: `${kpis.hh_programmed} / ${kpis.hh_available}`, sub: `Carga ${kpis.load_pct}%`, ok: kpis.load_ok },
    { label: "Cumplimiento PM", value: `${kpis.compliance_pct}%`, sub: `${kpis.orders_closed} de ${kpis.orders_total}`, ok: kpis.compliance_pct >= 90 },
    { label: "No Programado", value: `${kpis.non_planned_pct}%`, sub: "Meta < 15%", ok: kpis.non_planned_ok },
    { label: "Disponibilidad", value: `${kpis.availability_projected_pct}%`, sub: `Meta ${kpis.availability_target_pct}%`, ok: kpis.availability_projected_pct >= kpis.availability_target_pct },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {items.map((it, i) => (
        <div key={i} data-testid={`kpi-strip-${i}`} className={`p-4 border rounded-lg ${it.ok ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"}`}>
          <p className="text-[10px] uppercase font-bold tracking-widest text-slate-500">{it.label}</p>
          <p className="font-heading font-black text-2xl text-slate-900 mt-1">{it.value}</p>
          <p className="text-xs text-slate-500">{it.sub}</p>
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// A · COMPONENTES (Traceability / ADN de la flota) — TODO editable + reporte PDF
// ============================================================================
const COMPONENT_CATEGORIES = ["MOTOR", "TREN", "HIDRAULICO", "SEGURIDAD"];

function ComponentsTab({ equipment, components, canWrite, reload }) {
  const [filter, setFilter] = useState("all");
  const [semFilter, setSemFilter] = useState("all");
  const [sosFor, setSosFor] = useState(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);          // component being edited
  const [showReport, setShowReport] = useState(false);
  const eqById = useMemo(() => Object.fromEntries(equipment.map((e) => [e.id, e])), [equipment]);
  const filtered = components.filter((c) => {
    if (filter !== "all" && c.equipment_id !== filter) return false;
    if (semFilter !== "all" && c.semaforo !== semFilter) return false;
    return true;
  }).sort((a, b) => (b.pct_consumido || 0) - (a.pct_consumido || 0));

  const patch = async (cid, body) => {
    try {
      await api.put(`/maintenance/components/${cid}`, body);
      toast.success("Componente actualizado");
      reload();
    } catch (e) { toast.error(apiError(e)); }
  };

  const resetComp = async (cid) => {
    if (!window.confirm("¿Marcar como reemplazado? Se resetean las horas a 0.")) return;
    try {
      await api.post(`/maintenance/components/${cid}/reset`, {});
      toast.success("Componente reseteado");
      reload();
    } catch (e) { toast.error(apiError(e)); }
  };

  const deleteComp = async (cid) => {
    if (!window.confirm("Eliminar este componente permanentemente. ¿Continuar?")) return;
    try {
      await api.delete(`/maintenance/components/${cid}`);
      toast.success("Componente eliminado");
      reload();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="mt-4 space-y-3">
      <div className="flex flex-wrap gap-2 items-center">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger data-testid="mnt-comp-filter-equipment" className="w-56"><SelectValue placeholder="Equipo" /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">Todos los equipos</SelectItem>
            {equipment.map((e) => <SelectItem key={e.id} value={e.id}>{e.code} · {e.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={semFilter} onValueChange={setSemFilter}>
          <SelectTrigger data-testid="mnt-comp-filter-sem" className="w-56"><SelectValue placeholder="Semáforo" /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">Todos los semáforos</SelectItem>
            <SelectItem value="NEGRO">🖤 NEGRO · Paro inmediato</SelectItem>
            <SelectItem value="ROJO">🔴 ROJO · Cambio en 250h</SelectItem>
            <SelectItem value="AMARILLO">🟡 AMARILLO · Solicitud pedido</SelectItem>
            <SelectItem value="VERDE">🟢 VERDE · Monitoreo</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs text-slate-500 ml-2">{filtered.length} componentes</span>
        <div className="ml-auto flex gap-2">
          <Button data-testid="mnt-comp-report-btn" size="sm" variant="outline"
                  onClick={() => setShowReport(true)}>
            <FilePdf size={14} className="mr-1" /> Reporte General PDF
          </Button>
          {canWrite && (
            <Button data-testid="mnt-comp-new-btn" size="sm"
                    onClick={() => setCreating(true)} className="bg-orange-600 hover:bg-orange-700">
              <Plus size={14} className="mr-1" /> Nuevo componente
            </Button>
          )}
        </div>
      </div>

      <div className="overflow-x-auto border rounded-lg">
        <table className="min-w-full text-xs">
          <thead className="bg-slate-100 text-[10px] uppercase tracking-widest text-slate-500">
            <tr>
              <th className="p-2 text-left">Equipo</th>
              <th className="p-2 text-left">Categoría</th>
              <th className="p-2 text-left">Componente</th>
              <th className="p-2 text-left">N° Serie</th>
              <th className="p-2 text-right">Hrs Actuales</th>
              <th className="p-2 text-right">Vida Útil</th>
              <th className="p-2 text-right">% Consumido</th>
              <th className="p-2 text-center">Semáforo</th>
              <th className="p-2 text-left">Acción</th>
              <th className="p-2 text-right">Costo USD</th>
              {canWrite && <th className="p-2 text-center">Acciones</th>}
            </tr>
          </thead>
          <tbody className="divide-y">
            {filtered.map((c) => (
              <tr key={c.id} data-testid={`mnt-comp-row-${c.id}`} className="hover:bg-slate-50">
                <td className="p-2 font-mono text-[11px]">{eqById[c.equipment_id]?.code || "—"}</td>
                <td className="p-2">{c.category}</td>
                <td className="p-2 font-semibold">{c.name}</td>
                <td className="p-2 text-slate-500">
                  {canWrite ? (
                    <Input data-testid={`mnt-comp-serial-${c.id}`} type="text" defaultValue={c.serial || ""}
                            onBlur={(e) => e.target.value !== (c.serial || "") && patch(c.id, { serial: e.target.value })}
                            className="h-7 w-28 text-xs" placeholder="—" />
                  ) : (c.serial || "—")}
                </td>
                <td className="p-2 text-right">
                  {canWrite ? (
                    <Input data-testid={`mnt-comp-hours-${c.id}`} type="number" defaultValue={c.current_hours}
                            onBlur={(e) => Number(e.target.value) !== Number(c.current_hours) && patch(c.id, { current_hours: Number(e.target.value) })}
                            className="h-7 text-right w-24" />
                  ) : c.current_hours}
                </td>
                <td className="p-2 text-right">
                  {canWrite ? (
                    <Input data-testid={`mnt-comp-life-${c.id}`} type="number" defaultValue={c.life_hours_effective}
                            onBlur={(e) => Number(e.target.value) !== Number(c.life_hours_effective) && patch(c.id, { life_hours: Number(e.target.value) })}
                            className="h-7 text-right w-24" />
                  ) : c.life_hours_effective}
                </td>
                <td className="p-2 text-right font-mono">{c.pct_consumido}%</td>
                <td className="p-2 text-center">
                  <Badge className={`${SEM_COLOR[c.semaforo] || SEM_COLOR["N/A"]} border-0`}>{c.semaforo}</Badge>
                </td>
                <td className="p-2 text-slate-600 max-w-xs">{c.accion}</td>
                <td className="p-2 text-right">
                  {canWrite ? (
                    <Input data-testid={`mnt-comp-cost-${c.id}`} type="number" defaultValue={c.cost_usd_est || 0}
                            onBlur={(e) => Number(e.target.value) !== Number(c.cost_usd_est || 0) && patch(c.id, { cost_usd_est: Number(e.target.value) })}
                            className="h-7 text-right w-24" placeholder="0" />
                  ) : (c.cost_usd_est ? `$${Number(c.cost_usd_est).toLocaleString()}` : "—")}
                </td>
                {canWrite && (
                  <td className="p-2 text-center whitespace-nowrap">
                    <Button data-testid={`mnt-comp-edit-${c.id}`} size="sm" variant="outline"
                            onClick={() => setEditing(c)} className="h-7 text-[10px] mr-1" title="Editar nombre / categoría">
                      <PencilSimple size={12} />
                    </Button>
                    <Button data-testid={`mnt-comp-sos-${c.id}`} size="sm" variant="outline"
                            onClick={() => setSosFor(c)} className="h-7 text-[10px] mr-1">
                      <TestTube size={12} className="mr-1" /> SOS
                    </Button>
                    <Button data-testid={`mnt-comp-reset-${c.id}`} size="sm" variant="outline"
                            onClick={() => resetComp(c.id)} className="h-7 text-[10px] mr-1">
                      Reemplazado
                    </Button>
                    <Button data-testid={`mnt-comp-delete-${c.id}`} size="sm" variant="outline"
                            onClick={() => deleteComp(c.id)}
                            className="h-7 text-[10px] text-red-600 hover:bg-red-50" title="Eliminar componente">
                      <Trash size={12} />
                    </Button>
                  </td>
                )}
              </tr>
            ))}
            {!filtered.length && (
              <tr><td colSpan={11} className="p-8 text-center text-slate-500">
                Sin componentes. Usa <b>+ Nuevo componente</b> o registra equipos en <b>Flota</b> (auto-siembra).
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {sosFor && <SosDialog component={sosFor} equipment={eqById[sosFor.equipment_id]} canWrite={canWrite} onClose={() => setSosFor(null)} onSaved={reload} />}
      {creating && <ComponentEditDialog equipment={equipment} onClose={() => setCreating(false)} onSaved={reload} />}
      {editing && <ComponentEditDialog equipment={equipment} component={editing} onClose={() => setEditing(null)} onSaved={reload} />}
      {showReport && <GeneralReportDialog onClose={() => setShowReport(false)} />}
    </div>
  );
}

// ─── Dialog: crear/editar componente (nombre + categoría + resto) ──────
function ComponentEditDialog({ equipment, component, onClose, onSaved }) {
  const isEdit = Boolean(component);
  const [form, setForm] = useState({
    equipment_id: component?.equipment_id || (equipment[0]?.id || ""),
    category: component?.category || COMPONENT_CATEGORIES[0],
    name: component?.name || "",
    serial: component?.serial || "",
    current_hours: component?.current_hours ?? 0,
    life_hours: component?.life_hours_effective ?? component?.life_hours ?? 0,
    cost_usd_est: component?.cost_usd_est ?? 0,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Nombre del componente requerido"); return; }
    if (!form.equipment_id) { toast.error("Selecciona un equipo"); return; }
    setSaving(true);
    try {
      const payload = {
        equipment_id: form.equipment_id,
        category: form.category,
        name: form.name.trim(),
        serial: form.serial,
        current_hours: Number(form.current_hours) || 0,
        life_hours: Number(form.life_hours) || 0,
        cost_usd_est: Number(form.cost_usd_est) || 0,
      };
      if (isEdit) {
        await api.put(`/maintenance/components/${component.id}`, payload);
        toast.success("Componente actualizado");
      } else {
        await api.post("/maintenance/components", payload);
        toast.success("Componente creado");
      }
      onSaved(); onClose();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-white" data-testid="mnt-comp-dialog">
        <DialogHeader><DialogTitle>{isEdit ? "Editar componente" : "Nuevo componente"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <div className="col-span-2">
            <Label>Equipo *</Label>
            <Select value={form.equipment_id} onValueChange={(v) => set("equipment_id", v)}>
              <SelectTrigger data-testid="mnt-comp-dialog-equipment"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                {equipment.map((e) => (
                  <SelectItem key={e.id} value={e.id}>{e.code} · {e.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Categoría *</Label>
            <Select value={form.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger data-testid="mnt-comp-dialog-category"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                {COMPONENT_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Nombre *</Label>
            <Input data-testid="mnt-comp-dialog-name" value={form.name}
                    onChange={(e) => set("name", e.target.value)} placeholder="turbo, transmision, ..." />
          </div>
          <div>
            <Label>N° Serie</Label>
            <Input data-testid="mnt-comp-dialog-serial" value={form.serial}
                    onChange={(e) => set("serial", e.target.value)} placeholder="—" />
          </div>
          <div>
            <Label>Horas actuales</Label>
            <Input data-testid="mnt-comp-dialog-hours" type="number" value={form.current_hours}
                    onChange={(e) => set("current_hours", e.target.value)} />
          </div>
          <div>
            <Label>Vida útil (h)</Label>
            <Input data-testid="mnt-comp-dialog-life" type="number" value={form.life_hours}
                    onChange={(e) => set("life_hours", e.target.value)} />
          </div>
          <div>
            <Label>Costo USD estimado</Label>
            <Input data-testid="mnt-comp-dialog-cost" type="number" value={form.cost_usd_est}
                    onChange={(e) => set("cost_usd_est", e.target.value)} placeholder="0" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose} data-testid="mnt-comp-dialog-cancel">Cancelar</Button>
          <Button onClick={submit} disabled={saving} data-testid="mnt-comp-dialog-save" className="bg-orange-600 hover:bg-orange-700">
            {saving ? "Guardando..." : isEdit ? "Guardar" : "Crear"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Dialog: generar reporte general PDF ────────────────────────────────
function GeneralReportDialog({ onClose }) {
  const today = new Date().toISOString().slice(0, 10);
  const dflStart = new Date(Date.now() - 30 * 86400e3).toISOString().slice(0, 10);
  const [form, setForm] = useState({
    orient: "landscape",
    start: dflStart,
    end: today,
    include_components: true,
    include_orders: true,
    include_kpis: true,
  });
  const [gen, setGen] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const download = async () => {
    setGen(true);
    try {
      const params = new URLSearchParams({
        orient: form.orient,
        start: form.start,
        end: form.end,
        include_components: form.include_components,
        include_orders: form.include_orders,
        include_kpis: form.include_kpis,
      }).toString();
      const res = await api.get(`/maintenance/reports/general?${params}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `mantenimiento-general-${form.start}_${form.end}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Reporte generado");
      onClose();
    } catch (e) { toast.error(apiError(e)); }
    finally { setGen(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-white" data-testid="mnt-report-dialog">
        <DialogHeader><DialogTitle>Reporte General de Mantenimiento</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <Label>Orientación del papel</Label>
            <Select value={form.orient} onValueChange={(v) => set("orient", v)}>
              <SelectTrigger data-testid="mnt-report-orient"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="landscape">Horizontal (landscape) — recomendado</SelectItem>
                <SelectItem value="portrait">Vertical (portrait)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Fecha inicio</Label>
              <Input data-testid="mnt-report-start" type="date" value={form.start}
                      onChange={(e) => set("start", e.target.value)} />
            </div>
            <div>
              <Label>Fecha fin</Label>
              <Input data-testid="mnt-report-end" type="date" value={form.end}
                      onChange={(e) => set("end", e.target.value)} />
            </div>
          </div>
          <div className="space-y-1 pt-1">
            <Label>Secciones a incluir</Label>
            {[
              { k: "include_kpis", l: "KPIs semanales + alertas críticas" },
              { k: "include_components", l: "Estado completo de componentes (semáforos, vida útil, costo)" },
              { k: "include_orders", l: "Órdenes de trabajo del periodo" },
            ].map(({ k, l }) => (
              <label key={k} className="flex items-center gap-2 text-sm">
                <input data-testid={`mnt-report-${k}`} type="checkbox" checked={form[k]}
                       onChange={(e) => set(k, e.target.checked)} className="rounded" />
                {l}
              </label>
            ))}
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose} data-testid="mnt-report-cancel">Cancelar</Button>
          <Button onClick={download} disabled={gen} data-testid="mnt-report-download"
                  className="bg-orange-600 hover:bg-orange-700">
            <FilePdf size={14} className="mr-1" /> {gen ? "Generando..." : "Descargar PDF"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// SOS DIALOG — histórico + gráfica de tendencia (Fe/Cu/Al)
// ============================================================================
function SosDialog({ component, equipment, canWrite, onClose, onSaved }) {
  const [samples, setSamples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    sampled_at: new Date().toISOString().slice(0, 10),
    fe_ppm: 0, cu_ppm: 0, al_ppm: 0, si_ppm: 0,
    viscosidad: 0, tbn: 0, lab_id: "", notes: "",
    equipment_hours_at_sample: component.current_hours || 0,
  });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/maintenance/components/${component.id}/sos`);
      setSamples(r.data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [component.id]);   // eslint-disable-line react-hooks/exhaustive-deps

  const add = async () => {
    if (!form.sampled_at) return toast.error("Fecha requerida");
    try {
      await api.post(`/maintenance/components/${component.id}/sos`, {
        ...form,
        fe_ppm: Number(form.fe_ppm), cu_ppm: Number(form.cu_ppm), al_ppm: Number(form.al_ppm),
        si_ppm: Number(form.si_ppm), viscosidad: Number(form.viscosidad), tbn: Number(form.tbn),
        equipment_hours_at_sample: Number(form.equipment_hours_at_sample),
      });
      toast.success("Muestra SOS registrada");
      setForm({ ...form, fe_ppm: 0, cu_ppm: 0, al_ppm: 0, si_ppm: 0, notes: "", lab_id: "" });
      load();
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
  };

  const del = async (sid) => {
    if (!window.confirm("¿Eliminar esta muestra?")) return;
    try {
      await api.delete(`/maintenance/components/${component.id}/sos/${sid}`);
      load();
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
  };

  // Umbrales SOS del brief: Fe 100, Cu 50, Al 25
  const chartData = samples.map((s) => ({
    date: s.sampled_at,
    Fe: s.fe_ppm, Cu: s.cu_ppm, Al: s.al_ppm,
    hrs: s.equipment_hours_at_sample,
  }));

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl bg-white max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TestTube size={18} className="text-blue-700" />
            SOS · {equipment?.code} · {component.name}
          </DialogTitle>
          <p className="text-xs text-slate-500">Análisis espectrométrico de aceite · Umbrales críticos: Fe {">"} 100 ppm · Cu {">"} 50 ppm · Al {">"} 25 ppm (sobre-escritura semáforo a ROJO)</p>
        </DialogHeader>

        {loading ? <p className="text-slate-500 py-8 text-center">Cargando muestras...</p> : (
          <>
            {chartData.length > 0 ? (
              <div className="h-56 border rounded-lg p-2" data-testid="sos-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} label={{ value: "ppm", position: "insideLeft", angle: -90, offset: 12, style: { fontSize: 10 } }} />
                    <Tooltip contentStyle={{ fontSize: 11 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <ReferenceLine y={100} stroke="#EF4444" strokeDasharray="4 4" label={{ value: "Fe crítico", fontSize: 9, fill: "#EF4444" }} />
                    <ReferenceLine y={50} stroke="#F59E0B" strokeDasharray="4 4" label={{ value: "Cu crítico", fontSize: 9, fill: "#F59E0B" }} />
                    <ReferenceLine y={25} stroke="#7C3AED" strokeDasharray="4 4" label={{ value: "Al crítico", fontSize: 9, fill: "#7C3AED" }} />
                    <Line type="monotone" dataKey="Fe" stroke="#EF4444" strokeWidth={2} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="Cu" stroke="#F59E0B" strokeWidth={2} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="Al" stroke="#7C3AED" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-32 border rounded-lg flex items-center justify-center text-slate-400 text-sm">
                Sin muestras registradas — captura la primera abajo.
              </div>
            )}

            <div className="border rounded-lg overflow-hidden">
              <table className="min-w-full text-xs">
                <thead className="bg-slate-100 text-[10px] uppercase tracking-widest text-slate-500">
                  <tr>
                    <th className="p-2 text-left">Fecha</th>
                    <th className="p-2 text-right">Hrs Equipo</th>
                    <th className="p-2 text-right">Fe</th>
                    <th className="p-2 text-right">Cu</th>
                    <th className="p-2 text-right">Al</th>
                    <th className="p-2 text-right">Si</th>
                    <th className="p-2 text-right">Visc</th>
                    <th className="p-2 text-right">TBN</th>
                    <th className="p-2 text-left">Lab</th>
                    {canWrite && <th className="p-2"></th>}
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {samples.map((s) => {
                    const crit = s.fe_ppm > 100 || s.cu_ppm > 50 || s.al_ppm > 25;
                    return (
                      <tr key={s.id} data-testid={`sos-row-${s.id}`} className={crit ? "bg-red-50" : ""}>
                        <td className="p-2 font-mono">{s.sampled_at}</td>
                        <td className="p-2 text-right">{s.equipment_hours_at_sample}</td>
                        <td className={`p-2 text-right ${s.fe_ppm > 100 ? "font-black text-red-700" : ""}`}>{s.fe_ppm}</td>
                        <td className={`p-2 text-right ${s.cu_ppm > 50 ? "font-black text-amber-700" : ""}`}>{s.cu_ppm}</td>
                        <td className={`p-2 text-right ${s.al_ppm > 25 ? "font-black text-purple-700" : ""}`}>{s.al_ppm}</td>
                        <td className="p-2 text-right">{s.si_ppm}</td>
                        <td className="p-2 text-right">{s.viscosidad}</td>
                        <td className="p-2 text-right">{s.tbn}</td>
                        <td className="p-2 text-slate-500">{s.lab_id}</td>
                        {canWrite && (
                          <td className="p-2 text-right">
                            <Button size="sm" variant="ghost" onClick={() => del(s.id)} className="h-6 text-[10px] text-red-600">×</Button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                  {!samples.length && (
                    <tr><td colSpan={canWrite ? 10 : 9} className="p-4 text-center text-slate-400">Sin muestras</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {canWrite && (
              <div className="border rounded-lg p-3 bg-slate-50">
                <p className="text-xs font-black uppercase tracking-widest text-slate-600 mb-2">Nueva muestra</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <div><Label className="text-[10px] uppercase">Fecha</Label>
                    <Input data-testid="sos-date" type="date" value={form.sampled_at} onChange={(e) => setForm({ ...form, sampled_at: e.target.value })} /></div>
                  <div><Label className="text-[10px] uppercase">Hrs equipo</Label>
                    <Input type="number" value={form.equipment_hours_at_sample} onChange={(e) => setForm({ ...form, equipment_hours_at_sample: e.target.value })} /></div>
                  <div><Label className="text-[10px] uppercase">Lab ID</Label>
                    <Input placeholder="Ej: WEAR-2026-0891" value={form.lab_id} onChange={(e) => setForm({ ...form, lab_id: e.target.value })} /></div>
                  <div><Label className="text-[10px] uppercase">Notas</Label>
                    <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
                  <div><Label className="text-[10px] uppercase text-red-700">Fe (ppm) · {">"}100 crítico</Label>
                    <Input data-testid="sos-fe" type="number" value={form.fe_ppm} onChange={(e) => setForm({ ...form, fe_ppm: e.target.value })} /></div>
                  <div><Label className="text-[10px] uppercase text-amber-700">Cu (ppm) · {">"}50 crítico</Label>
                    <Input type="number" value={form.cu_ppm} onChange={(e) => setForm({ ...form, cu_ppm: e.target.value })} /></div>
                  <div><Label className="text-[10px] uppercase text-purple-700">Al (ppm) · {">"}25 crítico</Label>
                    <Input type="number" value={form.al_ppm} onChange={(e) => setForm({ ...form, al_ppm: e.target.value })} /></div>
                  <div><Label className="text-[10px] uppercase">Si (ppm)</Label>
                    <Input type="number" value={form.si_ppm} onChange={(e) => setForm({ ...form, si_ppm: e.target.value })} /></div>
                  <div><Label className="text-[10px] uppercase">Viscosidad</Label>
                    <Input type="number" value={form.viscosidad} onChange={(e) => setForm({ ...form, viscosidad: e.target.value })} /></div>
                  <div><Label className="text-[10px] uppercase">TBN</Label>
                    <Input type="number" value={form.tbn} onChange={(e) => setForm({ ...form, tbn: e.target.value })} /></div>
                </div>
                <div className="flex justify-end mt-3 gap-2">
                  <Button variant="outline" onClick={onClose}>Cerrar</Button>
                  <Button data-testid="sos-submit" onClick={add} className="bg-blue-700 hover:bg-blue-800">Guardar muestra</Button>
                </div>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// B · PROGRAMA SEMANAL (Órdenes de Trabajo)
// ============================================================================
function ProgramTab({ equipment, orders, kits, canWrite, kpis, orderKinds, reload }) {
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState({ equipment_id: "", date: new Date().toISOString().slice(0, 10), project_id: defaultProject(),
    kind: "PM250", motivo_real: "", bay: "Bahía 1", responsible: "", kit_id: "", kit_status: "pendiente",
    duration_h: 0, hh: 0 });
  const eqById = useMemo(() => Object.fromEntries(equipment.map((e) => [e.id, e])), [equipment]);
  const kitById = useMemo(() => Object.fromEntries(kits.map((k) => [k.id, k])), [kits]);

  const submit = async () => {
    if (!form.equipment_id || !form.date) return toast.error("Equipo y fecha son requeridos");
    if (!assertProject(form.project_id)) return;
    try {
      await api.post("/maintenance/orders", { ...form, hh: Number(form.hh), duration_h: Number(form.duration_h) });
      toast.success("OT creada");
      setDlg(false);
      setForm({ ...form, motivo_real: "", responsible: "" });
      reload();
    } catch (e) { toast.error(apiError(e)); }
  };

  const changeStatus = async (id, status) => {
    try { await api.put(`/maintenance/orders/${id}`, { status }); toast.success("OT actualizada"); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const changeKitStatus = async (id, kit_status) => {
    try { await api.put(`/maintenance/orders/${id}`, { kit_status }); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="mt-4 space-y-3">
      {kpis && !kpis.load_ok && (
        <div className="p-3 rounded-lg bg-red-50 border border-red-300 flex gap-2 text-sm">
          <WarningOctagon size={20} className="text-red-600 shrink-0" />
          <p><b>Carga excedida:</b> {kpis.load_pct}% programado (regla: ≤ 80%). Reasigna carga o extiende ventana.</p>
        </div>
      )}
      <div className="flex justify-between items-center">
        <p className="text-xs text-slate-500">Ventana semanal: {kpis?.week?.monday} → {kpis?.week?.sunday}</p>
        <div className="flex gap-2">
          <Button data-testid="mnt-export-weekly-pdf" onClick={() => {
            const API = process.env.REACT_APP_BACKEND_URL;
            window.open(`${API}/api/maintenance/reports/weekly-program`, "_blank");
          }} variant="outline">📄 PDF Programa</Button>
          {canWrite && <Button data-testid="mnt-order-new" onClick={() => setDlg(true)} className="bg-orange-600 hover:bg-orange-700">+ Nueva OT</Button>}
        </div>
      </div>

      <div className="overflow-x-auto border rounded-lg">
        <table className="min-w-full text-xs">
          <thead className="bg-slate-100 text-[10px] uppercase tracking-widest text-slate-500">
            <tr>
              <th className="p-2 text-left">Fecha</th>
              <th className="p-2 text-left">OT SAP</th>
              <th className="p-2 text-left">Equipo</th>
              <th className="p-2 text-right">Hrs Equipo</th>
              <th className="p-2 text-left">Tipo</th>
              <th className="p-2 text-left">Motivo Real</th>
              <th className="p-2 text-left">Bahía</th>
              <th className="p-2 text-left">Responsable</th>
              <th className="p-2 text-left">Estado KIT</th>
              <th className="p-2 text-right">Duración</th>
              <th className="p-2 text-right">HH</th>
              <th className="p-2 text-left">Estado</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {orders.map((o) => (
              <tr key={o.id} data-testid={`mnt-order-row-${o.id}`} className="hover:bg-slate-50">
                <td className="p-2 font-mono">{o.date} <ProjectBadge id={o.project_id} className="ml-1 align-middle" /></td>
                <td className="p-2 font-mono">{o.ot_number}</td>
                <td className="p-2">{eqById[o.equipment_id]?.code || "—"}</td>
                <td className="p-2 text-right">{eqById[o.equipment_id]?.total_hours || 0}</td>
                <td className="p-2"><Badge variant="outline">{o.kind}</Badge></td>
                <td className="p-2 text-slate-600 max-w-[240px] truncate" title={o.motivo_real}>{o.motivo_real}</td>
                <td className="p-2">{o.bay}</td>
                <td className="p-2">{o.responsible}</td>
                <td className="p-2">
                  {canWrite ? (
                    <Select value={o.kit_status} onValueChange={(v) => changeKitStatus(o.id, v)}>
                      <SelectTrigger className="h-7 w-32 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="pendiente">Pendiente</SelectItem>
                        <SelectItem value="reservado">Reservado</SelectItem>
                        <SelectItem value="entregado">Entregado</SelectItem>
                        <SelectItem value="no_aplica">N/A</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : o.kit_status}
                </td>
                <td className="p-2 text-right">{o.duration_h}h</td>
                <td className="p-2 text-right">{o.hh}</td>
                <td className="p-2">
                  {canWrite ? (
                    <Select value={o.status} onValueChange={(v) => changeStatus(o.id, v)}>
                      <SelectTrigger className="h-7 w-32 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="programado">Programado</SelectItem>
                        <SelectItem value="en_proceso">En proceso</SelectItem>
                        <SelectItem value="cerrado">Cerrado</SelectItem>
                        <SelectItem value="cancelado">Cancelado</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : o.status}
                </td>
              </tr>
            ))}
            {!orders.length && <tr><td colSpan={12} className="p-8 text-center text-slate-500">Sin OTs programadas aún.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="max-w-2xl bg-white">
          <DialogHeader><DialogTitle>Nueva Orden de Trabajo</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <ProjectField className="col-span-2" value={form.project_id} onChange={(v) => setForm({ ...form, project_id: v })} testid="mnt-order-project" />
            <div><Label className="overline-label">Fecha</Label>
              <Input data-testid="mnt-order-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
            <div><Label className="overline-label">Equipo</Label>
              <Select value={form.equipment_id} onValueChange={(v) => setForm({ ...form, equipment_id: v })}>
                <SelectTrigger data-testid="mnt-order-equipment"><SelectValue placeholder="Selecciona equipo" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {equipment.map((e) => <SelectItem key={e.id} value={e.id}>{e.code} · {e.name}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <div><Label className="overline-label">Tipo</Label>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger data-testid="mnt-order-kind"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {orderKinds.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <div><Label className="overline-label">Bahía</Label>
              <Input value={form.bay} onChange={(e) => setForm({ ...form, bay: e.target.value })} /></div>
            <div><Label className="overline-label">Responsable</Label>
              <Input value={form.responsible} onChange={(e) => setForm({ ...form, responsible: e.target.value })} /></div>
            <div><Label className="overline-label">Kit (opcional)</Label>
              <Select value={form.kit_id || "__none__"} onValueChange={(v) => setForm({ ...form, kit_id: v === "__none__" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="Sin kit" /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="__none__">Sin kit</SelectItem>
                  {kits.map((k) => <SelectItem key={k.id} value={k.id}>{k.name} · {k.kind}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <div><Label className="overline-label">Duración (h)</Label>
              <Input type="number" value={form.duration_h} onChange={(e) => setForm({ ...form, duration_h: e.target.value })} /></div>
            <div><Label className="overline-label">HH</Label>
              <Input type="number" value={form.hh} onChange={(e) => setForm({ ...form, hh: e.target.value })} /></div>
            <div className="col-span-2"><Label className="overline-label">Motivo real (horas equipo o componente)</Label>
              <Input placeholder="Ej: 4750h transmisión SC-01" value={form.motivo_real} onChange={(e) => setForm({ ...form, motivo_real: e.target.value })} /></div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDlg(false)}>Cancelar</Button>
            <Button data-testid="mnt-order-submit" onClick={submit} className="bg-orange-600 hover:bg-orange-700">Crear OT</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
// C · KITS
// ============================================================================
function KitsTab({ kits, canWrite, kitKinds, reload }) {
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState({ name: "", kind: "250H", contents: "", sap_reserve: "", location: "superficie", lead_time_days: 0, status: "pendiente" });
  const submit = async () => {
    if (!form.name) return toast.error("Nombre requerido");
    try {
      await api.post("/maintenance/kits", { ...form, lead_time_days: Number(form.lead_time_days) });
      toast.success("Kit creado"); setDlg(false); reload();
    } catch (e) { toast.error(apiError(e)); }
  };
  const changeStatus = async (id, status) => {
    try { await api.put(`/maintenance/kits/${id}`, { status }); reload(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="mt-4 space-y-3">
      {canWrite && <div className="flex justify-end"><Button data-testid="mnt-kit-new" onClick={() => setDlg(true)} className="bg-orange-600 hover:bg-orange-700">+ Nuevo Kit</Button></div>}
      <div className="overflow-x-auto border rounded-lg">
        <table className="min-w-full text-xs">
          <thead className="bg-slate-100 text-[10px] uppercase tracking-widest text-slate-500">
            <tr>
              <th className="p-2 text-left">Kit</th><th className="p-2 text-left">Tipo</th>
              <th className="p-2 text-left">Contenido</th><th className="p-2 text-left">Reserva SAP</th>
              <th className="p-2 text-left">Ubicación</th><th className="p-2 text-right">Lead Time</th>
              <th className="p-2 text-left">Estatus</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {kits.map((k) => (
              <tr key={k.id} data-testid={`mnt-kit-row-${k.id}`}>
                <td className="p-2 font-semibold">{k.name}</td>
                <td className="p-2"><Badge variant="outline">{k.kind}</Badge></td>
                <td className="p-2 text-slate-600 max-w-md">{k.contents}</td>
                <td className="p-2 font-mono">{k.sap_reserve || "—"}</td>
                <td className="p-2">{k.location}</td>
                <td className="p-2 text-right">{k.lead_time_days}d</td>
                <td className="p-2">
                  {canWrite ? (
                    <Select value={k.status} onValueChange={(v) => changeStatus(k.id, v)}>
                      <SelectTrigger className="h-7 w-32 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="pendiente">Pendiente</SelectItem>
                        <SelectItem value="solicitado">Solicitado</SelectItem>
                        <SelectItem value="reservado">Reservado</SelectItem>
                        <SelectItem value="recibido">Recibido</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : k.status}
                </td>
              </tr>
            ))}
            {!kits.length && <tr><td colSpan={7} className="p-8 text-center text-slate-500">Sin kits registrados.</td></tr>}
          </tbody>
        </table>
      </div>
      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader><DialogTitle>Nuevo Kit</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="overline-label">Nombre</Label><Input data-testid="mnt-kit-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label className="overline-label">Tipo</Label>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {kitKinds.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <div><Label className="overline-label">Contenido clave</Label><Input value={form.contents} onChange={(e) => setForm({ ...form, contents: e.target.value })} placeholder="Filtros aire+aceite+combustible, aceite 15W-40, empaque..." /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="overline-label">Reserva SAP</Label><Input value={form.sap_reserve} onChange={(e) => setForm({ ...form, sap_reserve: e.target.value })} /></div>
              <div><Label className="overline-label">Lead time (días)</Label><Input type="number" value={form.lead_time_days} onChange={(e) => setForm({ ...form, lead_time_days: e.target.value })} /></div>
            </div>
            <div><Label className="overline-label">Ubicación</Label>
              <Select value={form.location} onValueChange={(v) => setForm({ ...form, location: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="interior">Interior mina</SelectItem>
                  <SelectItem value="superficie">Superficie</SelectItem>
                </SelectContent>
              </Select></div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDlg(false)}>Cancelar</Button>
            <Button data-testid="mnt-kit-submit" onClick={submit} className="bg-orange-600 hover:bg-orange-700">Guardar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
// D · FORECAST 90-60-30
// ============================================================================
function ForecastTab({ forecast }) {
  if (!forecast) return <p className="text-slate-500 p-8">Cargando pronóstico...</p>;
  const buckets = [
    { key: "days_30", label: "PRÓXIMOS 30 DÍAS", color: "border-red-300 bg-red-50" },
    { key: "days_60", label: "PRÓXIMOS 31-60 DÍAS", color: "border-amber-300 bg-amber-50" },
    { key: "days_90", label: "PRÓXIMOS 61-90 DÍAS", color: "border-yellow-300 bg-yellow-50" },
  ];
  return (
    <div className="mt-4 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {buckets.map((b) => (
          <div key={b.key} className={`p-4 border rounded-lg ${b.color}`}>
            <p className="text-[10px] uppercase font-bold tracking-widest text-slate-600">{b.label}</p>
            <p className="font-heading font-black text-2xl text-slate-900 mt-1">
              USD {Number(forecast.capex_projection_usd[b.key]).toLocaleString()}
            </p>
            <p className="text-xs text-slate-500">{forecast.forecast[b.key].length} componentes a ROJO</p>
          </div>
        ))}
      </div>
      {buckets.map((b) => (
        <div key={b.key}>
          <p className="text-xs font-black uppercase tracking-widest text-slate-600 mb-1">{b.label}</p>
          <div className="overflow-x-auto border rounded-lg">
            <table className="min-w-full text-xs">
              <thead className="bg-slate-100 text-[10px] uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="p-2 text-left">Equipo</th><th className="p-2 text-left">Criticidad</th>
                  <th className="p-2 text-left">Componente</th><th className="p-2 text-right">Hrs Actuales</th>
                  <th className="p-2 text-right">Vida</th><th className="p-2 text-right">Días a ROJO</th>
                  <th className="p-2 text-right">Costo USD</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {forecast.forecast[b.key].map((r, i) => (
                  <tr key={i}>
                    <td className="p-2 font-mono">{r.equipment_code}</td>
                    <td className="p-2"><Badge variant={r.criticality === "A" ? "destructive" : "outline"}>{r.criticality}</Badge></td>
                    <td className="p-2">{r.component}</td>
                    <td className="p-2 text-right">{r.current_hours}</td>
                    <td className="p-2 text-right">{r.life_hours}</td>
                    <td className="p-2 text-right font-mono font-black">{r.days_to_red}d</td>
                    <td className="p-2 text-right">${Number(r.cost_usd_est).toLocaleString()}</td>
                  </tr>
                ))}
                {!forecast.forecast[b.key].length && <tr><td colSpan={7} className="p-4 text-center text-slate-400">Sin componentes en esta ventana</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// E · KPIs + Checklist Planeador
// ============================================================================
function KpisTab({ kpis, checklist }) {
  if (!kpis || !checklist) return <p className="text-slate-500 p-8">Cargando KPIs...</p>;
  const exportBoardPdf = () => {
    const API = process.env.REACT_APP_BACKEND_URL;
    window.open(`${API}/api/maintenance/reports/board-alerts`, "_blank");
  };
  return (
    <div className="mt-4 space-y-6">
      <div className="flex justify-end">
        <Button data-testid="mnt-export-board-pdf" onClick={exportBoardPdf} variant="outline">
          📄 PDF Alertas Junta Gerencia
        </Button>
      </div>
      <div>
        <p className="text-xs font-black uppercase tracking-widest text-slate-600 mb-2">3 Alertas críticas para junta con Gerencia Mina</p>
        {kpis.alerts.length === 0 ? (
          <div className="p-6 rounded-lg bg-emerald-50 border border-emerald-300 text-emerald-900">
            <p className="font-bold">✓ Sin alertas críticas en equipos de criticidad A. Flota estable.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {kpis.alerts.map((a, i) => (
              <div key={i} data-testid={`mnt-alert-${i}`} className={`p-3 rounded-lg border flex items-center gap-3 ${a.color === "NEGRO" ? "bg-slate-900 text-white border-slate-900" : "bg-red-50 border-red-300"}`}>
                <WarningOctagon size={22} weight="fill" className={a.color === "NEGRO" ? "text-red-400" : "text-red-600"} />
                <div className="flex-1">
                  <p className="font-black">{a.equipment} · {a.component}</p>
                  <p className="text-xs opacity-80">{a.message}</p>
                </div>
                <Badge className={`${SEM_COLOR[a.color]} border-0`}>{a.color}</Badge>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <div className="flex items-center gap-2 mb-2">
          <ListChecks size={18} className="text-orange-600" />
          <p className="text-xs font-black uppercase tracking-widest text-slate-600">Checklist del Planeador</p>
        </div>
        <div className="border rounded-lg divide-y">
          {checklist.checklist.map((c) => (
            <div key={c.id} data-testid={`mnt-checklist-${c.id}`} className="p-3 flex items-center gap-3">
              <div className={`h-6 w-6 rounded-full flex items-center justify-center text-white text-xs font-black ${c.ok === true ? "bg-emerald-500" : c.ok === false ? "bg-red-500" : "bg-slate-400"}`}>
                {c.ok === true ? "✓" : c.ok === false ? "✗" : "•"}
              </div>
              <p className="flex-1 text-sm text-slate-800">{c.label}</p>
              <span className="text-xs text-slate-500 font-mono">{c.value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// F · FLOTA (catálogo de equipos)
// ============================================================================
function FleetTab({ equipment, canWrite, reload }) {
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState({ code: "", name: "", brand: "Sandvik", model: "", criticality: "A", total_hours: 0, status: "activo", workshop: "interior", project_id: getOpsProject() });
  const opsProjects = useOpsProjects();
  const projectLabel = (id) => { const p = opsProjects.find((x) => x.id === id); return p ? (p.code || p.name) : ""; };
  const updateProject = async (id, project_id) => {
    try { await api.put(`/maintenance/equipment/${id}`, { project_id }); reloadEquipmentCache(); reload(); toast.success("Proyecto del equipo actualizado"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const [stats, setStats] = useState({});
  const [timelineFor, setTimelineFor] = useState(null);   // equipment object for modal

  useEffect(() => {
    api.get("/maintenance/equipment-stats")
      .then((r) => setStats(r.data || {}))
      .catch(() => setStats({}));
  }, [equipment.length]);

  const submit = async () => {
    if (!form.code || !form.name) return toast.error("Código y nombre son requeridos");
    try {
      await api.post("/maintenance/equipment", { ...form, total_hours: Number(form.total_hours) });
      reloadEquipmentCache();
      toast.success(`Equipo ${form.code} creado + componentes auto-sembrados`);
      setDlg(false);
      setForm({ ...form, code: "", name: "", model: "", total_hours: 0 });
      reload();
    } catch (e) { toast.error(apiError(e)); }
  };
  const updateHours = async (id, total_hours) => {
    try { await api.put(`/maintenance/equipment/${id}`, { total_hours: Number(total_hours) }); reload(); } catch (e) { toast.error(apiError(e)); }
  };
  const confirmDelete = useConfirmDelete();
  const del = async (id, code) => {
    const ok = await confirmDelete({
      title: `¿Eliminar equipo ${code}?`,
      itemLabel: code,
      description: "Se borrarán TODOS los componentes y OTs asociadas del equipo. Se registrará en el historial.",
    });
    if (!ok) return;
    try { await apiDelete(`/maintenance/equipment/${id}`, ok.reason); reloadEquipmentCache(); toast.success("Equipo eliminado"); reload(); } catch (e) { toast.error(apiError(e)); }
  };

  const openBacklogFor = (eq) => {
    // Handoff al panel Bitácora → Backlog. Guarda filtro y navega a la tab.
    try { sessionStorage.setItem("mnt.backlog.filter", JSON.stringify({ equipment_id: eq.id, code: eq.code, ts: Date.now() })); } catch (e) { /* noop */ }
    window.location.hash = "#backlog";
    // Cambia a la tab Bitácora
    const btn = document.querySelector('[data-testid="mnt-tab-bitacora"]');
    if (btn) btn.click();
    toast.info(`Backlog filtrado por ${eq.code}`);
  };

  return (
    <div className="mt-4 space-y-3">
      {canWrite && <div className="flex justify-end"><Button data-testid="mnt-eq-new" onClick={() => setDlg(true)} className="bg-orange-600 hover:bg-orange-700">+ Nuevo Equipo</Button></div>}
      <div className="overflow-x-auto border rounded-lg">
        <table className="min-w-full text-xs">
          <thead className="bg-slate-100 text-[10px] uppercase tracking-widest text-slate-500">
            <tr>
              <th className="p-2 text-left">Código</th><th className="p-2 text-left">Nombre</th>
              <th className="p-2 text-left">Marca</th><th className="p-2 text-left">Modelo</th>
              <th className="p-2 text-center">Crit.</th><th className="p-2 text-right">Hrs</th>
              <th className="p-2 text-center">OTs abiertas</th>
              <th className="p-2 text-left">Última insp.</th>
              <th className="p-2 text-left">Estado</th>
              <th className="p-2 text-left">Proyecto</th>
              <th className="p-2"></th>
              {canWrite && <th className="p-2"></th>}
            </tr>
          </thead>
          <tbody className="divide-y">
            {equipment.map((e) => {
              const s = stats[e.id] || {};
              const openOts = s.open_ots || 0;
              return (
              <tr key={e.id} data-testid={`mnt-eq-row-${e.id}`}>
                <td className="p-2 font-mono font-bold">{e.code}</td>
                <td className="p-2">{e.name}</td>
                <td className="p-2">{e.brand}</td>
                <td className="p-2">{e.model}</td>
                <td className="p-2 text-center"><Badge variant={e.criticality === "A" ? "destructive" : "outline"}>{e.criticality}</Badge></td>
                <td className="p-2 text-right">
                  {canWrite ? (
                    <Input type="number" defaultValue={e.total_hours}
                            onBlur={(ev) => ev.target.value !== String(e.total_hours) && updateHours(e.id, ev.target.value)}
                            className="h-7 w-24 text-right" />
                  ) : e.total_hours}
                </td>
                <td className="p-2 text-center">
                  <button
                    onClick={() => openBacklogFor(e)}
                    data-testid={`mnt-eq-ots-btn-${e.code}`}
                    disabled={openOts === 0}
                    className={`inline-flex items-center justify-center min-w-[36px] h-7 px-2 rounded-full text-xs font-black transition ${
                      openOts === 0
                        ? "bg-emerald-50 text-emerald-700 border border-emerald-200 cursor-default"
                        : openOts >= 3
                          ? "bg-red-600 text-white hover:bg-red-700 cursor-pointer"
                          : "bg-amber-500 text-white hover:bg-amber-600 cursor-pointer"
                    }`}
                    title={openOts > 0 ? `${openOts} OT abiertas · click para ver` : "Sin OTs abiertas"}
                  >
                    {openOts}
                  </button>
                </td>
                <td className="p-2 text-slate-500 text-[11px]">{s.last_inspection || "—"}</td>
                <td className="p-2">{e.status}</td>
                <td className="p-2">
                  {canWrite && opsProjects.length > 0 ? (
                    <select value={e.project_id || ""} onChange={(ev) => updateProject(e.id, ev.target.value)}
                            className="h-7 border rounded px-1 text-xs bg-white max-w-[140px]" data-testid={`mnt-eq-project-${e.code}`}>
                      <option value="">— Sin proyecto —</option>
                      {opsProjects.map((p) => <option key={p.id} value={p.id}>{p.code ? `${p.code} · ` : ""}{p.name}</option>)}
                    </select>
                  ) : <span className="text-xs text-blue-800 font-bold">{projectLabel(e.project_id) || "—"}</span>}
                </td>
                <td className="p-2 text-right">
                  <Button size="sm" variant="outline" data-testid={`mnt-eq-history-${e.code}`}
                    onClick={() => setTimelineFor(e)} className="h-7 text-[11px]">
                    Historial
                  </Button>
                </td>
                {canWrite && (
                  <td className="p-2 text-right">
                    <Button size="sm" variant="ghost" onClick={() => del(e.id, e.code)} className="text-red-600 h-7">Eliminar</Button>
                  </td>
                )}
              </tr>
            );})}
            {!equipment.length && (
              <tr><td colSpan={canWrite ? 11 : 10} className="p-8 text-center text-slate-500">
                Aún no hay equipos. Registra tu flota para arrancar el módulo.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <EquipmentTimelineDialog equipment={timelineFor} onOpenChange={() => setTimelineFor(null)} />

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader><DialogTitle>Nuevo Equipo</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="overline-label">Código</Label><Input data-testid="mnt-eq-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="SC-01" /></div>
            <div><Label className="overline-label">Nombre</Label><Input data-testid="mnt-eq-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Scoop LH621i" /></div>
            <div><Label className="overline-label">Marca</Label>
              <Select value={form.brand} onValueChange={(v) => setForm({ ...form, brand: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="Sandvik">Sandvik</SelectItem>
                  <SelectItem value="Epiroc">Epiroc</SelectItem>
                  <SelectItem value="Caterpillar">Caterpillar</SelectItem>
                  <SelectItem value="Komatsu">Komatsu</SelectItem>
                  <SelectItem value="Otro">Otro</SelectItem>
                </SelectContent>
              </Select></div>
            <div><Label className="overline-label">Modelo</Label><Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} /></div>
            <div><Label className="overline-label">Criticidad</Label>
              <Select value={form.criticality} onValueChange={(v) => setForm({ ...form, criticality: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="A">A · Crítico ({">"}88% disp)</SelectItem>
                  <SelectItem value="B">B · Importante</SelectItem>
                  <SelectItem value="C">C · Auxiliar</SelectItem>
                </SelectContent>
              </Select></div>
            <div><Label className="overline-label">Horas totales</Label><Input type="number" value={form.total_hours} onChange={(e) => setForm({ ...form, total_hours: e.target.value })} /></div>
            <div><Label className="overline-label">Estado</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="activo">Activo</SelectItem>
                  <SelectItem value="paro">En paro</SelectItem>
                  <SelectItem value="retirado">Retirado</SelectItem>
                </SelectContent>
              </Select></div>
            <div><Label className="overline-label">Taller</Label>
              <Select value={form.workshop} onValueChange={(v) => setForm({ ...form, workshop: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="interior">Interior mina</SelectItem>
                  <SelectItem value="superficie">Superficie</SelectItem>
                </SelectContent>
              </Select></div>
            {opsProjects.length > 0 && (
              <div><Label className="overline-label">Proyecto / sitio</Label>
                <select value={form.project_id || ""} onChange={(ev) => setForm({ ...form, project_id: ev.target.value })}
                        className="w-full h-9 border rounded-md px-2 text-sm bg-white" data-testid="mnt-eq-project">
                  <option value="">— Sin proyecto —</option>
                  {opsProjects.map((p) => <option key={p.id} value={p.id}>{p.code ? `${p.code} · ` : ""}{p.name}</option>)}
                </select></div>
            )}
          </div>
          <div className="text-xs text-slate-500 bg-blue-50 border border-blue-200 rounded p-2">
            Al crear el equipo se auto-siembran <b>19 componentes</b> (motor, tren, hidráulico, seguridad) con las horas del equipo. Turbo e inyectores arrancan al 50%.
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDlg(false)}>Cancelar</Button>
            <Button data-testid="mnt-eq-submit" onClick={submit} className="bg-orange-600 hover:bg-orange-700">Crear</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Confiabilidad · MTBF / MTTR / R = MTBF/(MTBF+MTTR)
// ---------------------------------------------------------------------------
function ReliabilityTab({ reliability, failures, equipment, canWrite, reload }) {
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState({
    equipment_id: "", started_at: "", ended_at: "",
    category: "unplanned", root_cause: "", notes: "",
    project_id: defaultProject(),
  });
  const [closeDlg, setCloseDlg] = useState(null);   // failure being closed
  const [closeForm, setCloseForm] = useState({ ended_at: "", root_cause: "", notes: "" });

  const fleet = reliability?.fleet || { mtbf_h: 0, mttr_h: 0, reliability_pct: 100, total_failures: 0, open_failures: 0 };
  const perEq = reliability?.per_equipment || [];

  const relColor = (pct) => pct >= 90 ? "text-emerald-600" : pct >= 80 ? "text-amber-600" : "text-red-600";
  const relBg = (pct) => pct >= 90 ? "bg-emerald-50 border-emerald-200" : pct >= 80 ? "bg-amber-50 border-amber-200" : "bg-red-50 border-red-200";

  const submitFailure = async () => {
    if (!form.equipment_id || !form.started_at) { toast.error("Equipo y fecha de inicio son obligatorios"); return; }
    if (!assertProject(form.project_id)) return;
    try {
      await api.post("/maintenance/failures", form);
      toast.success("Falla registrada");
      setDlg(false);
      setForm({ equipment_id: "", started_at: "", ended_at: "", category: "unplanned", root_cause: "", notes: "" });
      reload();
    } catch (e) { toast.error(apiError(e)); }
  };

  const submitClose = async () => {
    if (!closeForm.ended_at) { toast.error("Fecha de cierre requerida"); return; }
    try {
      await api.put(`/maintenance/failures/${closeDlg.id}/close`, closeForm);
      toast.success(`Falla cerrada · downtime registrado`);
      setCloseDlg(null); setCloseForm({ ended_at: "", root_cause: "", notes: "" });
      reload();
    } catch (e) { toast.error(apiError(e)); }
  };

  const removeFailure = async (fid) => {
    if (!window.confirm("¿Eliminar esta falla? El MTBF/MTTR se recalculará.")) return;
    try { await api.delete(`/maintenance/failures/${fid}`); toast.success("Falla eliminada"); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="mnt-reliability-tab">
      {/* Fleet KPI cards — MTBF + MTTR + R */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className={`rounded-lg border p-4 ${relBg(fleet.reliability_pct)}`} data-testid="rel-kpi-reliability">
          <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Confiabilidad flota</p>
          <p className={`font-heading font-black text-4xl mt-1 tabular-nums ${relColor(fleet.reliability_pct)}`}>
            {fleet.reliability_pct.toFixed(1)}<span className="text-xl">%</span>
          </p>
          <p className="text-[10px] text-slate-500 mt-1 font-mono">MTBF / (MTBF+MTTR)</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="rel-kpi-mtbf">
          <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">MTBF flota</p>
          <p className="font-heading font-black text-4xl mt-1 tabular-nums text-slate-900">
            {Math.round(fleet.mtbf_h).toLocaleString()}<span className="text-xl text-slate-400"> h</span>
          </p>
          <p className="text-[10px] text-slate-500 mt-1">Horas operativas / falla</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="rel-kpi-mttr">
          <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">MTTR flota</p>
          <p className="font-heading font-black text-4xl mt-1 tabular-nums text-slate-900">
            {fleet.mttr_h.toFixed(1)}<span className="text-xl text-slate-400"> h</span>
          </p>
          <p className="text-[10px] text-slate-500 mt-1">Horas de reparación / falla</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Fallas registradas</p>
          <p className="font-heading font-black text-4xl mt-1 tabular-nums text-slate-900">
            {fleet.total_failures}
            {fleet.open_failures > 0 && (
              <span className="text-lg text-red-600 ml-2">({fleet.open_failures} abiertas)</span>
            )}
          </p>
        </div>
      </div>

      {/* Per-equipment ranking */}
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading font-black text-lg text-slate-900 flex items-center gap-2">
            <Heartbeat size={18} weight="fill" className="text-red-600" /> Ranking de confiabilidad · por equipo
          </h3>
          {canWrite && (
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={reload} data-testid="rel-refresh-btn" className="gap-1.5">
                <ArrowClockwise size={13} /> Refrescar
              </Button>
              <Button data-testid="rel-register-failure-btn" size="sm" onClick={() => setDlg(true)} className="bg-red-600 hover:bg-red-700 gap-1.5">
                <Plus size={13} weight="bold" /> Registrar falla
              </Button>
            </div>
          )}
        </div>
        {perEq.length === 0 ? (
          <p className="text-sm text-slate-400 py-6 text-center">Sin equipos registrados aún.</p>
        ) : (
          <table className="w-full data-table" data-testid="rel-per-equipment-table">
            <thead>
              <tr>
                <th>Código</th><th>Equipo</th><th className="text-center">Crit.</th>
                <th className="text-right">Fallas</th>
                <th className="text-right">MTBF (h)</th>
                <th className="text-right">MTTR (h)</th>
                <th className="text-right">Confiabilidad</th>
              </tr>
            </thead>
            <tbody>
              {perEq.map((r) => (
                <tr key={r.equipment_id} data-testid={`rel-row-${r.code}`}>
                  <td className="font-mono font-bold text-xs">{r.code}</td>
                  <td className="text-sm">{r.name}</td>
                  <td className="text-center">
                    <Badge className={r.criticality === "A" ? "bg-red-100 text-red-700 border-red-300"
                        : r.criticality === "B" ? "bg-amber-100 text-amber-700 border-amber-300"
                        : "bg-slate-100 text-slate-700 border-slate-300"}>
                      {r.criticality || "—"}
                    </Badge>
                  </td>
                  <td className="text-right font-mono text-xs">
                    {r.n_failures}
                    {r.n_open > 0 && <span className="text-red-600 ml-1">({r.n_open}!)</span>}
                  </td>
                  <td className="text-right font-mono text-xs">{Math.round(r.mtbf_h).toLocaleString()}</td>
                  <td className="text-right font-mono text-xs">{r.mttr_h.toFixed(1)}</td>
                  <td className={`text-right font-mono font-bold ${relColor(r.reliability_pct)}`}
                      data-testid={`rel-pct-${r.code}`}>
                    {r.reliability_pct.toFixed(1)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Failure log */}
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <h3 className="font-heading font-black text-lg text-slate-900 mb-3 flex items-center gap-2">
          <WarningOctagon size={18} weight="fill" className="text-red-600" /> Bitácora de fallas
        </h3>
        {failures.length === 0 ? (
          <p className="text-sm text-slate-400 py-6 text-center">Sin fallas registradas aún.</p>
        ) : (
          <table className="w-full data-table" data-testid="rel-failures-table">
            <thead>
              <tr>
                <th>Equipo</th><th>Categoría</th><th>Inicio</th><th>Fin</th>
                <th className="text-right">Downtime (h)</th><th>Causa</th><th></th>
              </tr>
            </thead>
            <tbody>
              {failures.map((f) => (
                <tr key={f.id} data-testid={`rel-failure-${f.id}`} className={!f.ended_at ? "bg-red-50/50" : ""}>
                  <td className="font-mono text-xs font-bold">{f.equipment_code}</td>
                  <td>
                    <Badge className={f.category === "catastrophic" ? "bg-red-100 text-red-700 border-red-300"
                        : f.category === "minor" ? "bg-slate-100 text-slate-600 border-slate-300"
                        : "bg-amber-100 text-amber-700 border-amber-300"}>
                      {f.category}
                    </Badge>
                  </td>
                  <td className="text-xs font-mono">{f.started_at?.replace("T", " ").slice(0, 16)}</td>
                  <td className="text-xs font-mono">
                    {f.ended_at ? f.ended_at.replace("T", " ").slice(0, 16)
                                : <span className="text-red-600 font-bold">Abierta</span>}
                  </td>
                  <td className="text-right font-mono text-xs">{(f.downtime_h ?? 0).toFixed(1)}</td>
                  <td className="text-xs text-slate-600 max-w-xs truncate" title={f.root_cause}>{f.root_cause || "—"}</td>
                  <td className="text-right">
                    {canWrite && !f.ended_at && (
                      <Button size="sm" variant="outline" data-testid={`rel-close-${f.id}`}
                        onClick={() => { setCloseDlg(f); setCloseForm({ ended_at: new Date().toISOString().slice(0, 16), root_cause: f.root_cause || "", notes: f.notes || "" }); }}>
                        Cerrar
                      </Button>
                    )}
                    {canWrite && (
                      <Button size="sm" variant="ghost" className="ml-1 text-red-600"
                        data-testid={`rel-delete-${f.id}`}
                        onClick={() => removeFailure(f.id)}>×</Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Register failure dialog */}
      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">Registrar falla</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <ProjectField value={form.project_id} onChange={(v) => setForm({ ...form, project_id: v })} testid="rel-form-project" />
            <div><Label className="overline-label">Equipo</Label>
              <Select value={form.equipment_id} onValueChange={(v) => setForm({ ...form, equipment_id: v })}>
                <SelectTrigger data-testid="rel-form-equipment"><SelectValue placeholder="Selecciona un equipo" /></SelectTrigger>
                <SelectContent className="bg-white max-h-60">
                  {equipment.map((eq) => (
                    <SelectItem key={eq.id} value={eq.id}>{eq.code} — {eq.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="overline-label">Inicio</Label>
                <Input type="datetime-local" data-testid="rel-form-started" value={form.started_at}
                  onChange={(e) => setForm({ ...form, started_at: e.target.value })} /></div>
              <div><Label className="overline-label">Fin (opcional)</Label>
                <Input type="datetime-local" data-testid="rel-form-ended" value={form.ended_at}
                  onChange={(e) => setForm({ ...form, ended_at: e.target.value })} /></div>
            </div>
            <div><Label className="overline-label">Categoría</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="rel-form-category"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="unplanned">No planificada</SelectItem>
                  <SelectItem value="catastrophic">Catastrófica</SelectItem>
                  <SelectItem value="minor">Menor</SelectItem>
                </SelectContent>
              </Select></div>
            <div><Label className="overline-label">Causa raíz</Label>
              <Input data-testid="rel-form-root-cause" value={form.root_cause}
                onChange={(e) => setForm({ ...form, root_cause: e.target.value })}
                placeholder="Ej. Falla motor por sobrecalentamiento" /></div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setDlg(false)}>Cancelar</Button>
              <Button data-testid="rel-form-submit" onClick={submitFailure}
                className="bg-red-600 hover:bg-red-700">Registrar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Close failure dialog */}
      <Dialog open={!!closeDlg} onOpenChange={(v) => !v && setCloseDlg(null)}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">
            Cerrar falla · {closeDlg?.equipment_code}
          </DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">Inicio: <b className="font-mono">{closeDlg?.started_at}</b></p>
            <div><Label className="overline-label">Fin (obligatorio)</Label>
              <Input type="datetime-local" data-testid="rel-close-ended" value={closeForm.ended_at}
                onChange={(e) => setCloseForm({ ...closeForm, ended_at: e.target.value })} /></div>
            <div><Label className="overline-label">Causa raíz</Label>
              <Input data-testid="rel-close-root-cause" value={closeForm.root_cause}
                onChange={(e) => setCloseForm({ ...closeForm, root_cause: e.target.value })} /></div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setCloseDlg(null)}>Cancelar</Button>
              <Button data-testid="rel-close-submit" onClick={submitClose}
                className="bg-emerald-600 hover:bg-emerald-700">Cerrar falla</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Operador · MTBF / MTTR / DF / DM / U desde captura de operador
// ---------------------------------------------------------------------------
// Cruza `operations_entries` (horas capturadas por operador) con
// `maint_failures` en un rango de fechas. Fórmulas estándar minería:
//   HC = 24 × días · HR = Σ downtime cerrado · HO = Σ hours operador
//   DF = (HC-HR)/HC · DM = HO/(HO+HR) · U = HO/(HC-HR)
// ---------------------------------------------------------------------------
function OperatorMetricsTab() {
  const iso = (d) => d.toISOString().slice(0, 10);
  const today = new Date();
  const start = new Date(); start.setDate(today.getDate() - 30);
  const [range, setRange] = useState({ date_from: iso(start), date_to: iso(today) });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (r = range) => {
    if (!r.date_from || !r.date_to) return;
    setLoading(true);
    try {
      const res = await api.get("/maintenance/operator-metrics", { params: r });
      setData(res.data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(range); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const fleet = data?.fleet || {};
  const perEq = data?.per_equipment || [];
  const dfColor = (pct) => pct >= 90 ? "text-emerald-600" : pct >= 80 ? "text-amber-600" : "text-red-600";

  const applyRange = () => load(range);
  const quickRange = (days) => {
    const d1 = new Date(); const d0 = new Date(); d0.setDate(d1.getDate() - days);
    const nr = { date_from: iso(d0), date_to: iso(d1) };
    setRange(nr); load(nr);
  };

  return (
    <div className="space-y-6" data-testid="mnt-operator-tab">
      {/* Range picker + quick shortcuts */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 flex flex-wrap items-end gap-3">
        <div><Label className="overline-label">Desde</Label>
          <Input type="date" data-testid="op-metrics-from" value={range.date_from}
            onChange={(e) => setRange({ ...range, date_from: e.target.value })} /></div>
        <div><Label className="overline-label">Hasta</Label>
          <Input type="date" data-testid="op-metrics-to" value={range.date_to}
            onChange={(e) => setRange({ ...range, date_to: e.target.value })} /></div>
        <Button data-testid="op-metrics-apply" onClick={applyRange}
          className="bg-slate-800 hover:bg-slate-900 gap-1.5" disabled={loading}>
          <ArrowClockwise size={13} /> Actualizar
        </Button>
        <div className="flex items-center gap-1 ml-auto">
          <Button variant="outline" size="sm" onClick={() => quickRange(7)} data-testid="op-quick-7d">7d</Button>
          <Button variant="outline" size="sm" onClick={() => quickRange(30)} data-testid="op-quick-30d">30d</Button>
          <Button variant="outline" size="sm" onClick={() => quickRange(90)} data-testid="op-quick-90d">90d</Button>
        </div>
      </div>

      {/* Fleet KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="op-kpi-df">
          <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Disp. Física</p>
          <p className={`font-heading font-black text-4xl mt-1 tabular-nums ${dfColor(fleet.df_pct ?? 100)}`}>
            {(fleet.df_pct ?? 100).toFixed(1)}<span className="text-xl">%</span>
          </p>
          <p className="text-[10px] text-slate-500 mt-1 font-mono">(HC − HR) / HC</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="op-kpi-dm">
          <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Disp. Mecánica</p>
          <p className={`font-heading font-black text-4xl mt-1 tabular-nums ${dfColor(fleet.dm_pct ?? 100)}`}>
            {(fleet.dm_pct ?? 100).toFixed(1)}<span className="text-xl">%</span>
          </p>
          <p className="text-[10px] text-slate-500 mt-1 font-mono">HO / (HO + HR)</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="op-kpi-util">
          <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Utilización</p>
          <p className={`font-heading font-black text-4xl mt-1 tabular-nums ${dfColor(fleet.utilizacion_pct ?? 0)}`}>
            {(fleet.utilizacion_pct ?? 0).toFixed(1)}<span className="text-xl">%</span>
          </p>
          <p className="text-[10px] text-slate-500 mt-1 font-mono">HO / (HC − HR)</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">HO / HR / N fallas</p>
          <p className="font-heading font-black text-2xl mt-1 tabular-nums text-slate-900">
            <span title="Horas operativas del operador">{Math.round(fleet.horas_operativas ?? 0)}h</span>
            <span className="text-slate-300 mx-2">/</span>
            <span title="Horas de reparación" className="text-red-600">{Math.round(fleet.horas_reparacion ?? 0)}h</span>
            <span className="text-slate-300 mx-2">/</span>
            <span>{fleet.n_failures ?? 0}</span>
          </p>
          <p className="text-[10px] text-slate-500 mt-1">
            {data?.range?.days ?? 0} días · {fleet.n_equipment ?? 0} equipos
          </p>
        </div>
      </div>

      {/* Per-equipment matrix */}
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <h3 className="font-heading font-black text-lg text-slate-900 mb-3 flex items-center gap-2">
          <Gauge size={18} weight="fill" className="text-orange-600" />
          Matriz por equipo · datos del operador
        </h3>
        {perEq.length === 0 ? (
          <p className="text-sm text-slate-400 py-6 text-center">
            {loading ? "Cargando…" : "Sin datos en el rango seleccionado."}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full data-table" data-testid="op-metrics-table">
              <thead>
                <tr>
                  <th>Código</th><th>Equipo</th>
                  <th className="text-right" title="Horómetro actual del equipo">Horóm.</th>
                  <th className="text-right" title="Entradas de captura del operador">Entradas</th>
                  <th className="text-right" title="Horas operativas capturadas">HO (h)</th>
                  <th className="text-right" title="Horas de reparación (fallas cerradas)">HR (h)</th>
                  <th className="text-right">Fallas</th>
                  <th className="text-right">MTBF</th>
                  <th className="text-right">MTTR</th>
                  <th className="text-right">Confiab.</th>
                  <th className="text-right">DF</th>
                  <th className="text-right">DM</th>
                  <th className="text-right">Util.</th>
                </tr>
              </thead>
              <tbody>
                {perEq.map((r) => (
                  <tr key={r.equipment_id} data-testid={`op-row-${r.code}`}>
                    <td className="font-mono font-bold text-xs">{r.code}</td>
                    <td className="text-sm">{r.name}</td>
                    <td className="text-right font-mono text-xs">{Math.round(r.horometro_actual).toLocaleString()}</td>
                    <td className="text-right font-mono text-xs">{r.n_operator_entries}</td>
                    <td className="text-right font-mono text-xs">{r.horas_operativas.toFixed(1)}</td>
                    <td className="text-right font-mono text-xs text-red-600">{r.horas_reparacion.toFixed(1)}</td>
                    <td className="text-right font-mono text-xs">
                      {r.n_failures}
                      {r.n_open > 0 && <span className="text-red-600 ml-0.5">!</span>}
                    </td>
                    <td className="text-right font-mono text-xs">{Math.round(r.mtbf_h)}</td>
                    <td className="text-right font-mono text-xs">{r.mttr_h.toFixed(1)}</td>
                    <td className={`text-right font-mono font-bold ${dfColor(r.confiabilidad_pct)}`}>
                      {r.confiabilidad_pct.toFixed(1)}%
                    </td>
                    <td className={`text-right font-mono font-bold ${dfColor(r.df_pct)}`}
                        data-testid={`op-df-${r.code}`}>
                      {r.df_pct.toFixed(1)}%
                    </td>
                    <td className={`text-right font-mono font-bold ${dfColor(r.dm_pct)}`}
                        data-testid={`op-dm-${r.code}`}>
                      {r.dm_pct.toFixed(1)}%
                    </td>
                    <td className={`text-right font-mono font-bold ${dfColor(r.utilizacion_pct)}`}
                        data-testid={`op-util-${r.code}`}>
                      {r.utilizacion_pct.toFixed(1)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-[10px] text-slate-400 mt-3 font-mono leading-relaxed">
          <b>HC</b> horas calendario (24 × días) · <b>HO</b> horas operativas capturadas por operador ·
          <b> HR</b> horas de reparación (fallas cerradas) · <b>DF</b> = (HC−HR)/HC ·
          <b> DM</b> = HO/(HO+HR) · <b>Util.</b> = HO/(HC−HR) · <b>MTBF</b> = (HO−HR)/N ·
          <b> MTTR</b> = HR/N cerradas · <b>Confiab.</b> = MTBF/(MTBF+MTTR)
        </p>
      </div>
    </div>
  );
}


// ============================================================================
// BATHTUB TAB · Análisis por Equipo — Curva de la Bañera
// Cruza λ, MTBF, MTTR, R(t), Utilización y Costos mes a mes para detectar la
// fase (Mortalidad Infantil / Vida Útil / Desgaste) y sugerir la decisión
// técnica. Base normativa: ISO 14224, UNE-EN 13306, ISO 55000 · Moubray RCM II.
// ============================================================================
function BathtubTab({ equipment }) {
  const [eid, setEid] = useState(equipment[0]?.id || "");
  const [months, setMonths] = useState(12);
  const [replacementCost, setReplacementCost] = useState(0);   // 0 = auto
  const [lifeYears, setLifeYears] = useState(10);
  const [discountRate, setDiscountRate] = useState(0.10);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => { if (!eid && equipment[0]) setEid(equipment[0].id); }, [equipment, eid]);
  useEffect(() => {
    if (!eid) return;
    setLoading(true);
    const params = new URLSearchParams({
      months: String(months),
      replacement_cost_usd: String(replacementCost),
      life_years: String(lifeYears),
      discount_rate: String(discountRate),
    }).toString();
    api.get(`/maintenance/equipment/${eid}/bathtub?${params}`)
      .then((r) => setData(r.data))
      .catch((e) => { toast.error(apiError(e)); setData(null); })
      .finally(() => setLoading(false));
  }, [eid, months, replacementCost, lifeYears, discountRate]);

  const PHASE_STYLE = {
    mortalidad_infantil: { bg: "bg-sky-50", border: "border-sky-300", text: "text-sky-800", icon: "🍼" },
    vida_util:           { bg: "bg-emerald-50", border: "border-emerald-300", text: "text-emerald-800", icon: "✓" },
    desgaste:            { bg: "bg-red-50", border: "border-red-300", text: "text-red-800", icon: "⚠" },
  };

  const arrow = (dir) => dir === "up" ? "▲" : dir === "down" ? "▼" : "→";
  const trendColor = (field, dir) => {
    const inverted = new Set(["lambda", "mttr"]);
    const good = inverted.has(field) ? (dir === "down") : (dir === "up");
    if (dir === "flat") return "text-slate-500";
    return good ? "text-emerald-700" : "text-red-700";
  };

  const chartCommon = { margin: { top: 8, right: 12, left: 0, bottom: 0 } };

  return (
    <div className="mt-4 space-y-4" data-testid="mnt-bathtub">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={eid} onValueChange={setEid}>
          <SelectTrigger className="w-72" data-testid="bathtub-equipment"><SelectValue placeholder="Equipo" /></SelectTrigger>
          <SelectContent className="bg-white">
            {equipment.map((e) => (
              <SelectItem key={e.id} value={e.id}>{e.code} · {e.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={String(months)} onValueChange={(v) => setMonths(Number(v))}>
          <SelectTrigger className="w-40" data-testid="bathtub-months"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            {[6, 12, 18, 24, 36].map((n) => <SelectItem key={n} value={String(n)}>Últimos {n} meses</SelectItem>)}
          </SelectContent>
        </Select>
        {data && (
          <span className="text-xs text-slate-500 ml-2">
            Rango: <b>{data.range_from} → {data.range_to}</b> · {data.totals.failures} fallas · {data.totals.ops_h}h operadas
          </span>
        )}
      </div>

      {loading && <p className="text-sm text-slate-400 italic p-8 text-center">Calculando indicadores RAM…</p>}

      {data && !loading && (() => {
        const st = PHASE_STYLE[data.phase] || PHASE_STYLE.vida_util;
        const last = data.last || {};
        return (<>
          <div className="bg-white border rounded-lg p-4">
            <p className="overline-label text-slate-500 mb-2">Ficha del último mes · {last.label}</p>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3 text-xs">
              <BathtubKpi label="λ Tasa de fallos" value={`${last.lambda} f/h`} trend={data.trends.lambda} arrow={arrow} color={trendColor("lambda", data.trends.lambda.direction)} />
              <BathtubKpi label="MTBF" value={`${last.mtbf} h`} trend={data.trends.mtbf} arrow={arrow} color={trendColor("mtbf", data.trends.mtbf.direction)} />
              <BathtubKpi label="MTTR" value={`${last.mttr} h`} trend={data.trends.mttr} arrow={arrow} color={trendColor("mttr", data.trends.mttr.direction)} />
              <BathtubKpi label="R(24h)" value={`${last.reliab}%`} trend={data.trends.reliab} arrow={arrow} color={trendColor("reliab", data.trends.reliab.direction)} />
              <BathtubKpi label="Utilización" value={`${last.utiliz}%`} trend={data.trends.utiliz} arrow={arrow} color={trendColor("utiliz", data.trends.utiliz.direction)} />
              <BathtubKpi label="Costo mes / acum." value={`$${(last.cost_month||0).toLocaleString()} / $${(last.cost_acum||0).toLocaleString()}`} trend={{delta_pct:0,direction:"flat"}} arrow={arrow} color="text-slate-700" />
            </div>
          </div>

          <div className={`border-2 rounded-lg p-4 ${st.bg} ${st.border}`} data-testid="bathtub-phase">
            <p className="overline-label text-slate-600">Fase detectada (Curva de la Bañera)</p>
            <div className="flex flex-wrap items-center gap-3 mt-1">
              <span className={`font-heading font-black text-2xl ${st.text}`}>
                {st.icon} {data.phase_label}
              </span>
              <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-white border border-slate-200 text-slate-700">
                λ: {arrow(data.trends.lambda.direction)} {data.trends.lambda.delta_pct}%
              </span>
              <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-white border border-slate-200 text-slate-700">
                MTBF: {arrow(data.trends.mtbf.direction)} {data.trends.mtbf.delta_pct}%
              </span>
            </div>
            <p className={`mt-2 text-sm font-semibold ${st.text}`}>
              Decisión técnica: {data.decision}
            </p>
          </div>

          {/* ANÁLISIS LCC · Life Cycle Cost (ISO 55000) */}
          {data.lcc && (() => {
            const lcc = data.lcc;
            const lccStyle = {
              mantener:            { bg: "bg-emerald-50", border: "border-emerald-300", text: "text-emerald-800", icon: "✓" },
              planear_reemplazo:   { bg: "bg-amber-50", border: "border-amber-300", text: "text-amber-800", icon: "◐" },
              reemplazar_ya:       { bg: "bg-red-50", border: "border-red-400", text: "text-red-800", icon: "⚠" },
            }[lcc.verdict] || { bg: "bg-slate-50", border: "border-slate-300", text: "text-slate-800", icon: "?" };
            return (
              <div className={`border-2 rounded-lg p-4 ${lccStyle.bg} ${lccStyle.border}`} data-testid="bathtub-lcc">
                <div className="flex items-start justify-between flex-wrap gap-2 mb-2">
                  <div>
                    <p className="overline-label text-slate-600">Análisis LCC · Life Cycle Cost (ISO 55000)</p>
                    <p className={`font-heading font-black text-lg ${lccStyle.text}`}>
                      {lccStyle.icon} {lcc.verdict_label}
                    </p>
                  </div>
                  <div className="flex gap-2 items-end text-xs">
                    <div>
                      <Label className="text-[10px] uppercase font-bold text-slate-500">Costo reposición USD</Label>
                      <Input type="number" value={replacementCost} min={0} step={1000}
                             onChange={(e) => setReplacementCost(Number(e.target.value) || 0)}
                             className="h-8 w-32 text-right" placeholder="Auto" data-testid="lcc-replacement" />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase font-bold text-slate-500">Vida útil (años)</Label>
                      <Input type="number" value={lifeYears} min={2} max={30}
                             onChange={(e) => setLifeYears(Math.max(2, Math.min(30, Number(e.target.value) || 10)))}
                             className="h-8 w-20 text-right" data-testid="lcc-life" />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase font-bold text-slate-500">Tasa % anual</Label>
                      <Input type="number" value={Math.round(discountRate * 100)} min={0} max={50} step={1}
                             onChange={(e) => setDiscountRate(Math.max(0, Math.min(50, Number(e.target.value) || 10)) / 100)}
                             className="h-8 w-20 text-right" data-testid="lcc-rate" />
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs mt-2">
                  <div className="bg-white/70 border border-slate-200 rounded p-2">
                    <p className="text-[9px] uppercase tracking-widest font-bold text-slate-500">CAE Mantener</p>
                    <p className="font-heading font-black text-slate-900 text-lg">${lcc.cae_mantener.toLocaleString()}</p>
                    <p className="text-[10px] text-slate-500">costo anual actual</p>
                  </div>
                  <div className="bg-white/70 border border-slate-200 rounded p-2">
                    <p className="text-[9px] uppercase tracking-widest font-bold text-slate-500">CAE Reponer</p>
                    <p className="font-heading font-black text-slate-900 text-lg">${lcc.cae_reponer.toLocaleString()}</p>
                    <p className="text-[10px] text-slate-500">amortiz. {lcc.life_years}a @ {Math.round(lcc.discount_rate * 100)}%</p>
                  </div>
                  <div className="bg-white/70 border border-slate-200 rounded p-2">
                    <p className="text-[9px] uppercase tracking-widest font-bold text-slate-500">Ratio mantener/reponer</p>
                    <p className={`font-heading font-black text-lg ${lcc.ratio_pct >= 100 ? "text-red-700" : lcc.ratio_pct >= 70 ? "text-amber-700" : "text-emerald-700"}`}>
                      {lcc.ratio_pct}%
                    </p>
                    <p className="text-[10px] text-slate-500">≥100% ya no conviene</p>
                  </div>
                  <div className="bg-white/70 border border-slate-200 rounded p-2">
                    <p className="text-[9px] uppercase tracking-widest font-bold text-slate-500">Meses al break-even</p>
                    <p className="font-heading font-black text-slate-900 text-lg">
                      {lcc.months_to_break_even !== null && lcc.months_to_break_even !== undefined ? lcc.months_to_break_even : "—"}
                    </p>
                    <p className="text-[10px] text-slate-500">a ritmo actual de gasto</p>
                  </div>
                  <div className="bg-white/70 border border-slate-200 rounded p-2">
                    <p className="text-[9px] uppercase tracking-widest font-bold text-slate-500">Reposición USD</p>
                    <p className="font-heading font-black text-slate-900 text-lg">
                      ${lcc.replacement_cost_usd.toLocaleString()}
                    </p>
                    <p className="text-[10px] text-slate-500">
                      {replacementCost === 0 ? "estimado 40× costo/mes" : "personalizado"}
                    </p>
                  </div>
                </div>
                <p className="text-[10px] italic text-slate-500 mt-2 border-t border-slate-200 pt-2">
                  Fórmula CAE reponer: P · i(1+i)ⁿ / ((1+i)ⁿ − 1). ISO 55000 Gestión de Activos.
                  Ajusta el costo real de reposición para refinar el veredicto.
                </p>
              </div>
            );
          })()}

          <div className="grid md:grid-cols-2 gap-3">
            <BathtubChart title="Tasa de fallos λ (fallas/h)" testid="chart-lambda">
              <LineChart data={data.series} {...chartCommon}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="label" fontSize={10} />
                <YAxis fontSize={10} />
                <Tooltip />
                <Line type="monotone" dataKey="lambda" stroke="#dc2626" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </BathtubChart>

            <BathtubChart title="MTBF vs MTTR (horas)" testid="chart-mtbf-mttr">
              <LineChart data={data.series} {...chartCommon}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="label" fontSize={10} />
                <YAxis fontSize={10} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="mtbf" name="MTBF" stroke="#059669" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="mttr" name="MTTR" stroke="#ea580c" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </BathtubChart>

            <BathtubChart title="Confiabilidad vs Utilización (%)" testid="chart-reliab-util">
              <LineChart data={data.series} {...chartCommon}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="label" fontSize={10} />
                <YAxis fontSize={10} domain={[0, 100]} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="reliab" name="R(24h) %" stroke="#2563eb" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="utiliz" name="Utilización %" stroke="#7c3aed" strokeWidth={2} dot={{ r: 3 }} />
                <ReferenceLine y={80} stroke="#94a3b8" strokeDasharray="3 3" label={{ value: "Meta 80%", fontSize: 9, fill: "#64748b" }} />
              </LineChart>
            </BathtubChart>

            <BathtubChart title="Costo mensual y acumulado (USD)" testid="chart-costs">
              <LineChart data={data.series} {...chartCommon}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="label" fontSize={10} />
                <YAxis fontSize={10} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="cost_month" name="Costo mes" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="cost_acum" name="Acumulado" stroke="#0f172a" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </BathtubChart>
          </div>

          <div className="bg-white border rounded-lg p-4">
            <p className="overline-label text-slate-500 mb-2">Matriz de decisión técnica por fase (marco teórico)</p>
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead className="bg-slate-100 text-[10px] uppercase tracking-widest text-slate-500">
                  <tr>
                    <th className="p-2 text-left">Fase</th>
                    <th className="p-2 text-center">λ</th>
                    <th className="p-2 text-center">MTBF</th>
                    <th className="p-2 text-left">Acción recomendada</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {[
                    { p: "mortalidad_infantil", n: "Mortalidad Infantil", lam: "▼ Decreciente", mtbf: "▲ Creciente", acc: "Ajustar · garantías · puesta a punto · PM inicial" },
                    { p: "vida_util",           n: "Vida Útil",           lam: "→ Estable/Bajo", mtbf: "→ Estable/Alto", acc: "Operar · sostener plan preventivo estándar" },
                    { p: "desgaste",            n: "Desgaste",            lam: "▲ Creciente",   mtbf: "▼ Decreciente",  acc: "Intervenir/reemplazar · overhaul · LCC · plan de baja" },
                  ].map((row) => (
                    <tr key={row.p} className={row.p === data.phase ? `${PHASE_STYLE[row.p].bg} font-bold ${PHASE_STYLE[row.p].text}` : ""}>
                      <td className="p-2">{PHASE_STYLE[row.p].icon} {row.n}{row.p === data.phase && " ← equipo aquí"}</td>
                      <td className="p-2 text-center">{row.lam}</td>
                      <td className="p-2 text-center">{row.mtbf}</td>
                      <td className="p-2">{row.acc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[10px] text-slate-500 italic mt-2">
              Marco: {data.framework.norms.join(" · ")} — {data.framework.authors.join(" · ")}.
            </p>
          </div>
        </>);
      })()}
    </div>
  );
}

function BathtubKpi({ label, value, trend, arrow, color }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded p-2">
      <p className="text-[9px] uppercase tracking-widest font-bold text-slate-500">{label}</p>
      <p className="font-heading font-black text-slate-900 text-lg leading-tight">{value}</p>
      <p className={`text-[10px] font-mono ${color}`}>{arrow(trend.direction)} {trend.delta_pct}%</p>
    </div>
  );
}

function BathtubChart({ title, testid, children }) {
  return (
    <div className="bg-white border rounded-lg p-3" data-testid={testid}>
      <p className="overline-label text-slate-500 mb-1">{title}</p>
      <ResponsiveContainer width="100%" height={200}>{children}</ResponsiveContainer>
    </div>
  );
}

/**
 * EquipmentTimelineDialog · Modal con historial unificado del equipo:
 * OTs + Inspecciones + Fallas en un solo timeline (más reciente primero).
 * Consume GET /maintenance/equipment/{id}/timeline.
 */
function EquipmentTimelineDialog({ equipment, onOpenChange }) {
  const [events, setEvents] = useState(null);
  useEffect(() => {
    if (!equipment) { setEvents(null); return; }
    api.get(`/maintenance/equipment/${equipment.id}/timeline`)
      .then((r) => setEvents(r.data || []))
      .catch(() => setEvents([]));
  }, [equipment]);
  const tone = {
    ot: "bg-blue-100 text-blue-800 border-blue-300",
    failure: "bg-red-100 text-red-800 border-red-300",
    inspection: "bg-amber-100 text-amber-800 border-amber-300",
  };
  return (
    <Dialog open={!!equipment} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="mnt-eq-timeline-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            Historial de {equipment?.code} · {equipment?.name}
          </DialogTitle>
        </DialogHeader>
        {events === null ? (
          <p className="text-sm text-slate-400 text-center py-8">Cargando historial…</p>
        ) : events.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-8">
            Sin eventos registrados para este equipo. Los eventos aparecen aquí conforme se crean OTs, inspecciones o fallas.
          </p>
        ) : (
          <div className="relative pl-6 border-l-2 border-slate-200 space-y-3" data-testid="mnt-eq-timeline">
            {events.map((ev, i) => (
              <div key={i} className="relative" data-testid={`mnt-eq-timeline-row-${i}`}>
                <div className={`absolute -left-[27px] top-1 w-3 h-3 rounded-full border-2 ${
                  ev.type === "ot" ? "bg-blue-500 border-blue-700" :
                  ev.type === "failure" ? "bg-red-500 border-red-700" :
                  "bg-amber-500 border-amber-700"
                }`} />
                <div className="bg-white border border-slate-200 rounded p-2.5">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[9px] font-black uppercase px-1.5 py-0.5 rounded border ${tone[ev.type] || "bg-slate-100 text-slate-700"}`}>
                      {ev.type}
                    </span>
                    <span className="text-[11px] font-mono text-slate-500">{ev.date || "—"}</span>
                    {ev.status && (
                      <span className="text-[9px] font-bold ml-auto text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded">
                        {ev.status}
                      </span>
                    )}
                  </div>
                  <p className="font-bold text-sm text-slate-800">{ev.title}</p>
                  {ev.detail && <p className="text-xs text-slate-600 mt-0.5">{ev.detail}</p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

